import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAppData } from '../AppContext';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('specs');
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const { products: storeProducts, settings } = useAppData();

  const phone = settings?.whatsappPhone || '919820325285';

  const products = {
    zinga: {
      id: 'zinga',
      name: 'Zinga',
      category: 'Film Galvanizing',
      badge: 'Best Seller',
      shortDescription: '1 pack coating containing 96% zinc and providing cathodic protection',
      fullDescription: 'Zinga is a 1 pack coating containing 96% zinc and providing cathodic protection. It is a single-component, high-performance metallic coating that promotes a cathodic film protection system.',
      price: 'Contact Us',
      stock: 'In Stock',
      specs: [
        { label: 'Zinc Content', value: '96% (±1%) in Dry Film' },
        { label: 'Zinc Purity', value: '99.995% pure' },
        { label: 'Colour', value: 'Steel Grey' },
        { label: 'Temperature', value: '-40°C to 150°C' },
        { label: 'pH Resistance', value: '3.5 to 12.5 pH' },
        { label: 'Toxicity', value: 'Non Toxic (AS/NSZ 4020)' },
        { label: 'Shelf Life', value: 'Unlimited' },
        { label: 'UV Resistance', value: 'Excellent' },
        { label: 'Coverage', value: '3.62 m²/kg or 9.67 m²/litre' },
        { label: 'Packaging', value: '1 kg and 5 kg' },
        { label: 'Compatible Thinner', value: 'Zingasolv' }
      ],
      features: [
        '96% zinc content provides superior cathodic protection',
        'Unlimited shelf life - no waste or expiry concerns',
        'Easy application - can be applied like paint',
        'Self-healing properties protect even damaged areas',
        'Non-toxic and environmentally friendly',
        'Excellent UV resistance for long-term durability'
      ],
      applications: [
        'Steel structures',
        'Bridges and infrastructure',
        'Marine environments',
        'Industrial equipment',
        'Pipelines and tanks',
        'Railway applications'
      ]
    },
    zingalu: {
      id: 'zingalu',
      name: 'Zingalu',
      category: 'Organic Zinc Coating',
      badge: 'Premium',
      shortDescription: 'A 1 component high performance standalone organic zinc rich coating',
      fullDescription: 'Zingalu is a 1 component high performance standalone organic zinc rich coating. It combines zinc with aluminum flakes for enhanced protection.',
      price: 'Contact Us',
      stock: 'In Stock',
      specs: [
        { label: 'Zinc Content', value: '90% ultra pure Zinc + 4% Aluminum' },
        { label: 'Zinc Purity', value: '99.995% pure' },
        { label: 'Colour', value: 'Grey with Aluminum Shine' },
        { label: 'Temperature', value: '-40°C to 150°C' },
        { label: 'pH Resistance', value: '3.5 to 12.5 pH' },
        { label: 'Toxicity', value: 'Non Toxic (AS/NSZ 4020)' },
        { label: 'Shelf Life', value: '2 years' },
        { label: 'UV Resistance', value: 'Excellent' },
        { label: 'Coverage', value: '4.26 m²/kg or 10.5 m²/litre' },
        { label: 'Packaging', value: '5 kg' },
        { label: 'Compatible Thinner', value: 'Zingasolv' }
      ],
      features: [
        '90% zinc plus 4% aluminum for enhanced protection',
        'Standalone coating - no primer needed',
        'Excellent coverage rate',
        '2-year shelf life for flexibility',
        'Professional grade performance',
        'Suitable for various substrates'
      ],
      applications: [
        'Steel structures',
        'Industrial equipment',
        'Marine applications',
        'Infrastructure projects',
        'Chemical plants',
        'Power generation facilities'
      ]
    },
    aquazinga: {
      id: 'aquazinga',
      name: 'AquaZinga',
      category: 'Water Based',
      badge: 'Eco-Friendly',
      shortDescription: 'A 2 pack 100% water based anti-corrosive system',
      fullDescription: 'AquaZinga is a 2 pack 100% water based anti-corrosive system based on inorganic zinc silicates. It offers excellent abrasion resistance and withstands severe corrosive environments.',
      price: 'Contact Us',
      stock: 'In Stock',
      specs: [
        { label: 'Zinc Content', value: '92% (±2%) in Dry Film' },
        { label: 'Zinc Purity', value: '99.995% pure' },
        { label: 'Colour', value: 'Steel Grey' },
        { label: 'Temperature', value: '-90°C to 600°C' },
        { label: 'pH Resistance', value: '5.5 to 9.5 pH' },
        { label: 'Toxicity', value: 'Non Toxic (AS/NSZ 4020)' },
        { label: 'Shelf Life', value: '12 months' },
        { label: 'UV Resistance', value: 'Excellent' },
        { label: 'Coverage', value: '3.62 m²/kg or 9.67 m²/litre' },
        { label: 'Packaging', value: '10 kg (7.6 kg powder + 2.4 kg binder)' },
        { label: 'Compatible Thinner', value: 'No Thinner needed' }
      ],
      features: [
        '100% water-based - environmentally friendly',
        '92% zinc content with excellent coverage',
        'Wide temperature range (-90°C to 600°C)',
        'Excellent abrasion resistance',
        'Suitable for severe corrosive environments',
        'No thinner required'
      ],
      applications: [
        'High-temperature environments',
        'Chemical exposure areas',
        'Industrial equipment',
        'Pipelines',
        'Storage tanks',
        'Processing plants'
      ]
    }
  };

  // Smart default: pick the closest hardcoded spec set based on ID prefix / category
  const getDefaultProduct = (productId, category) => {
    const pid = (productId || '').toLowerCase();
    const cat = (category || '').toLowerCase();
    if (pid.startsWith('aquazinga') || cat === 'aquazinga') return products.aquazinga;
    if (pid.startsWith('zingalu') || cat === 'zingaalu' || cat === 'zingalu') return products.zingalu;
    return products.zinga; // covers zinga*, zingasolv, alu-*, zingatarfree, accessories
  };

  const storeProduct = storeProducts.find(p => p.id === id);
  const product = products[id] || getDefaultProduct(id, storeProduct?.category);

  // Build images array from store (supports multi-image) or fallback to URL field
  const productImages = storeProduct
    ? (storeProduct.images?.length > 0 ? storeProduct.images : (storeProduct.image ? [storeProduct.image] : []))
    : [];
  // Merge: store data takes priority; fallback to hardcoded defaults
  const displayProduct = storeProduct ? {
    ...product,
    name: storeProduct.name,
    category: storeProduct.category || product.category,
    badge: storeProduct.badge || product.badge,
    stock: storeProduct.stock || product.stock,
    price: storeProduct.price ? `₹${storeProduct.price}` : 'Contact Us',
    shortDescription: storeProduct.shortDescription || storeProduct.description || product.shortDescription,
    fullDescription: storeProduct.description || product.fullDescription,
    // Use store specs/features/applications if admin has defined them, else keep hardcoded per-product defaults
    specs: (storeProduct.specs && storeProduct.specs.length > 0) ? storeProduct.specs : (product.specs || []),
    features: (storeProduct.features && storeProduct.features.length > 0) ? storeProduct.features : (product.features || []),
    applications: (storeProduct.applications && storeProduct.applications.length > 0) ? storeProduct.applications : (product.applications || []),
  } : product;

  const handleEnquire = () => {
    const productName = displayProduct.name;
    const msg = encodeURIComponent(`Hi, I'm interested in *${productName}* (Qty: ${quantity}). Please share pricing and availability details.`);
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="product-detail-page">

      {/* ── Lightbox ── */}
      {lightbox && productImages[activeImg] && (
        <div className="pd-lightbox" onClick={() => setLightbox(false)}>
          <button className="pd-lb-close" onClick={() => setLightbox(false)} aria-label="Close">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <div className="pd-lb-inner" onClick={e => e.stopPropagation()}>
            <img src={productImages[activeImg]} alt={displayProduct.name} className="pd-lb-img" />
            {productImages.length > 1 && (
              <div className="pd-lb-thumbs">
                {productImages.map((img, i) => (
                  <img key={i} src={img} alt={`view ${i+1}`}
                    className={`pd-lb-thumb ${activeImg === i ? 'pd-lb-thumb-active' : ''}`}
                    onClick={() => setActiveImg(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="page-header">
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/products">Products</Link>
            <span>/</span>
            <span className="current">{displayProduct.name}</span>
          </nav>
        </div>
      </div>

      <div className="container">
        <div className="product-detail-grid">
          <div className="product-image-section">
            <div className="product-image-large" onClick={() => productImages[activeImg] && setLightbox(true)}>
              {productImages[activeImg]
                ? <img src={productImages[activeImg]} alt={displayProduct.name} className="pd-main-img" />
                : <div className="image-placeholder">
                    <svg width="150" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                    <span className="product-initial-large">{displayProduct.name.charAt(0)}</span>
                  </div>
              }
              <div className="product-badge-large">{displayProduct.badge || product.badge}</div>
            </div>

            <div className="product-thumbnails">
              {productImages.length > 0
                ? productImages.map((img, i) => (
                    <div key={i} className={`thumbnail ${activeImg === i ? 'active' : ''}`} onClick={() => setActiveImg(i)} style={{cursor:'pointer'}}>
                      <img src={img} alt={`view ${i+1}`} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:6}} />
                    </div>
                  ))
                : [0,1,2].map(i => (
                    <div key={i} className={`thumbnail ${i===0?'active':''}`}>
                      <div className="thumbnail-placeholder">{displayProduct.name.charAt(0)}</div>
                    </div>
                  ))
              }
            </div>
          </div>

          <div className="product-info-section">
            <div className="product-category-badge">{displayProduct.category}</div>
            <h1 className="product-title-large">{displayProduct.name}</h1>

            <div className="product-price-section">
              <div className="price-tag-large">
                {displayProduct.price}
              </div>
              <div className="stock-badge">
                <span className="stock-indicator"></span>
                <span>{displayProduct.stock || product.stock}</span>
              </div>
            </div>

            <p className="product-description-large">
              {displayProduct.fullDescription}
            </p>

            <div className="quantity-section">
              <label>Quantity:</label>
              <div className="quantity-controls">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                />
                <button onClick={() => setQuantity(quantity + 1)}>+</button>
              </div>
            </div>

            <div className="action-buttons">
              <button onClick={handleEnquire} className="btn-enquiry-wa">
                <svg viewBox="0 0 32 32" width="20" height="20" fill="currentColor">
                  <path d="M16 2C8.28 2 2 8.28 2 16c0 2.44.66 4.72 1.8 6.68L2 30l7.52-1.96A13.94 13.94 0 0016 30c7.72 0 14-6.28 14-14S23.72 2 16 2zm0 25.6c-2.2 0-4.28-.58-6.08-1.6l-.44-.26-4.46 1.16 1.18-4.34-.28-.46A11.59 11.59 0 014.4 16C4.4 9.6 9.6 4.4 16 4.4c6.4 0 11.6 5.2 11.6 11.6 0 6.4-5.2 11.6-11.6 11.6zm6.38-8.52c-.34-.18-2.02-1-2.34-1.1-.32-.12-.56-.18-.78.18-.22.34-.86 1.1-1.06 1.34-.2.22-.38.24-.72.08-.34-.18-1.44-.54-2.74-1.7-1.02-.9-1.7-2-1.9-2.34-.2-.34-.02-.52.14-.7.16-.16.34-.4.52-.6.18-.2.22-.34.34-.56.12-.22.06-.42-.02-.6-.08-.18-.78-1.86-1.06-2.54-.28-.66-.56-.56-.78-.58h-.66c-.22 0-.58.08-.88.42-.3.34-1.14 1.12-1.14 2.72s1.16 3.16 1.32 3.38c.18.22 2.28 3.48 5.52 4.88.78.32 1.38.52 1.86.68.78.24 1.5.2 2.06.12.62-.1 1.92-.78 2.2-1.54.28-.76.28-1.4.2-1.54-.08-.14-.28-.22-.62-.4z"/>
                </svg>
                Enquire on WhatsApp
              </button>
              <Link to="/contact" className="btn-request-quote">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                Request Quote
              </Link>
            </div>

            <div className="trust-badges">
              <div className="trust-badge">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                <span>Quality Assured</span>
              </div>
              <div className="trust-badge">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
                <span>Secure Payment</span>
              </div>
              <div className="trust-badge">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="3" width="15" height="13"></rect>
                  <polygon points="16 8 20 8 23 11 23 16 16 8 16 8"></polygon>
                  <circle cx="5.5" cy="18.5" r="2.5"></circle>
                  <circle cx="18.5" cy="18.5" r="2.5"></circle>
                </svg>
                <span>Fast Delivery</span>
              </div>
            </div>
          </div>
        </div>

        <div className="product-tabs">
          {/* determine which tabs to show */}
          {(() => {
            const specs    = displayProduct.specs    || product.specs    || [];
            const features = displayProduct.features || product.features || [];
            const apps     = displayProduct.applications || product.applications || [];
            const desc     = displayProduct.fullDescription || '';
            const hasSpecs    = specs.length > 0;
            const hasFeatures = features.length > 0;
            const hasApps     = apps.length > 0;
            return (
              <>
                <div className="tab-buttons">
                  {hasSpecs && (
                    <button className={`tab-button ${activeTab === 'specs' ? 'active' : ''}`} onClick={() => setActiveTab('specs')}>Specifications</button>
                  )}
                  {hasFeatures && (
                    <button className={`tab-button ${activeTab === 'features' ? 'active' : ''}`} onClick={() => setActiveTab('features')}>Features</button>
                  )}
                  {hasApps && (
                    <button className={`tab-button ${activeTab === 'applications' ? 'active' : ''}`} onClick={() => setActiveTab('applications')}>Applications</button>
                  )}
                  {desc && (
                    <button className={`tab-button ${activeTab === 'description' ? 'active' : ''}`} onClick={() => setActiveTab('description')}>Description</button>
                  )}
                </div>
                <div className="tab-content">
                  {activeTab === 'specs' && hasSpecs && (
                    <div className="specs-table">
                      <table><tbody>
                        {specs.map((spec, i) => (
                          <tr key={i}>
                            <td className="spec-label-cell">{spec.label}</td>
                            <td className="spec-value-cell">{spec.value}</td>
                          </tr>
                        ))}
                      </tbody></table>
                    </div>
                  )}
                  {activeTab === 'features' && hasFeatures && (
                    <div className="features-list"><ul>
                      {features.map((feature, i) => (
                        <li key={i}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                          {feature}
                        </li>
                      ))}
                    </ul></div>
                  )}
                  {activeTab === 'applications' && hasApps && (
                    <div className="applications-grid">
                      {apps.map((app, i) => (
                        <div key={i} className="application-card">
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                          <span>{app}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {activeTab === 'description' && desc && (
                    <div className="desc-tab-content">{desc}</div>
                  )}
                  {/* fallback: show description if active tab has no content */}
                  {!hasSpecs && !hasFeatures && !hasApps && activeTab !== 'description' && desc && (
                    <div className="desc-tab-content">{desc}</div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
